import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PublicNavbar from "../Components/PublicNavbar";
import PublicTracking from "./PublicTracking";
import httpclient from "../Utils";

const validateFields = (credentials) => {
  const fieldErrors = {};

  const username = credentials.username.trim();
  if (!username) {
    fieldErrors.username = "Username is required.";
  } else if (username.length < 3) {
    fieldErrors.username = "Username must be at least 3 characters.";
  }

  const password = credentials.password;
  if (!password) {
    fieldErrors.password = "Password is required.";
  }

  return fieldErrors;
};

const PublicTrackingLoginForm = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateFields(credentials);
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await httpclient.post("/delivery/check-tracking-credentials", {
        username: credentials.username.trim(),
        password: credentials.password,
      });

      if (res.data.status === 200) {
        onLoginSuccess(credentials.username.trim());
      } else {
        setError(res.data.message || "Invalid username or password.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Invalid username or password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f0f2f5", minHeight: "100vh" }}>
      <PublicNavbar />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 70px)",
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
            maxWidth: 420,
            p: { xs: 3, sm: 4 },
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            backgroundColor: "#FFFFFF",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="700"
            sx={{ fontFamily: "Poppins", mb: 0.5, color: "#111827" }}
          >
            Tracking Login
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", mb: 3 }}>
            Please sign in to view your delivery tracking details.
          </Typography>

          <TextField
            fullWidth
            name="username"
            label="Username"
            variant="outlined"
            value={credentials.username}
            onChange={handleChange}
            error={!!fieldErrors.username}
            helperText={fieldErrors.username || " "}
            disabled={loading}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircleIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={credentials.password}
            onChange={handleChange}
            error={!!fieldErrors.password}
            helperText={fieldErrors.password || " "}
            disabled={loading}
            sx={{ mb: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: "action.active" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    size="small"
                    disabled={loading}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              height: 48,
            }}
          >
            {loading ? "Logging in..." : "Login"}
          </Button>
        </Paper>
      </Box>
    </Box>
  );
};

const PublicTrackingAuthGate = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  if (!isAuthenticated) {
    return <PublicTrackingLoginForm onLoginSuccess={() => setIsAuthenticated(true)} />;
  }

  return <PublicTracking />;
};

export default PublicTrackingAuthGate;
